import { effect, inject, Injectable } from '@angular/core';
import { ActiveUserService } from '../active-user.service';
import { AuthService } from '../auth.service';
import { SessionService } from '../session.service';
import { TRACKJS_CLIENT, TrackjsClient } from './trackjs-client';

export const TRACKJS_IDENTITY_METADATA = {
  ACTIVE_USER_ID: 'activeUserId',
  ACTING_AS: 'actingAs',
  GROUP_ID: 'groupId',
} as const;

export interface TrackjsIdentitySnapshot {
  authUserId: number | null;
  activeUserId: number | null;
  isActingAsOther: boolean;
  groupId: string | null;
}

export function applyTrackjsIdentity(
  trackjs: TrackjsClient,
  snapshot: TrackjsIdentitySnapshot,
): void {
  if (!trackjs.isInstalled()) {
    return;
  }

  if (snapshot.authUserId === null) {
    trackjs.configure({ userId: undefined });
    trackjs.removeMetadata(TRACKJS_IDENTITY_METADATA.ACTIVE_USER_ID);
    trackjs.removeMetadata(TRACKJS_IDENTITY_METADATA.ACTING_AS);
    trackjs.removeMetadata(TRACKJS_IDENTITY_METADATA.GROUP_ID);
    return;
  }

  trackjs.configure({ userId: String(snapshot.authUserId) });

  if (snapshot.activeUserId !== null) {
    trackjs.addMetadata(
      TRACKJS_IDENTITY_METADATA.ACTIVE_USER_ID,
      String(snapshot.activeUserId),
    );
  }

  trackjs.addMetadata(
    TRACKJS_IDENTITY_METADATA.ACTING_AS,
    snapshot.isActingAsOther ? 'true' : 'false',
  );

  if (snapshot.groupId !== null && snapshot.groupId !== '') {
    trackjs.addMetadata(TRACKJS_IDENTITY_METADATA.GROUP_ID, snapshot.groupId);
  } else {
    trackjs.removeMetadata(TRACKJS_IDENTITY_METADATA.GROUP_ID);
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrackjsIdentityService {
  private readonly trackjs = inject(TRACKJS_CLIENT);
  private readonly session = inject(SessionService);
  private readonly activeUser = inject(ActiveUserService);
  private readonly auth = inject(AuthService);

  private readonly syncIdentityEffect = effect(() => {
    if (!this.session.sessionReady()) {
      return;
    }

    const authUserId = this.activeUser.authUserId();
    const activeUserId = this.activeUser.activeUserId();
    const isActingAsOther = this.activeUser.isActingAsOther();
    const groupId = this.auth.newuserProfile()?.GROUPID ?? null;

    applyTrackjsIdentity(this.trackjs, {
      authUserId,
      activeUserId,
      isActingAsOther,
      groupId,
    });
  });

  initialize(): void {
    // Reactive identity sync is registered at construction time.
  }
}
